import { ColumnMethod, KycSummary } from '../../../services/benchmarkService';
import { count } from '../BenchmarkVocabulary';

/**
 * Wording for supplied-sample runs, shared by the run list, the banner and the
 * provenance drawer so the three never describe the same run differently.
 */

/** How many supplied-sample runs the scorer keeps per project before pruning. */
export const SUPPLIED_RUN_RETENTION = 10;

export const RETENTION_NOTE =
    `Only the ${SUPPLIED_RUN_RETENTION} most recent supplied-sample runs per project are kept. Export a sample from its results page if you need to keep it longer.`;

/** The file name, or a plain description when the records were sent inline. */
export function sampleName(sourceName: string | null | undefined): string {
    if (!sourceName) return 'Unnamed sample';
    return sourceName === 'inline' ? 'Records sent with the request' : sourceName;
}

/** "16 alerts → 16 cases". Either side may be missing on an older run. */
export function alertsToCases(rowsSupplied: number | null | undefined, cases: number | null | undefined): string {
    const supplied = rowsSupplied === null || rowsSupplied === undefined ? null : `${count(rowsSupplied)} alerts`;
    const scored = cases === null || cases === undefined ? null : `${count(cases)} ${cases === 1 ? 'case' : 'cases'}`;
    if (supplied && scored) return `${supplied} → ${scored}`;
    return supplied || scored || '—';
}

/**
 * When the supplied KYC was dated, relative to each customer's alerts.
 *
 * "Dated before their alerts" is the claim that lifts the look-ahead caveat,
 * so it is only made when every supplied record qualifies.
 */
export function kycDating(kyc: KycSummary | null | undefined): string | null {
    if (!kyc) return null;
    const supplied = kyc.suppliedCustomers ?? 0;
    const pointInTime = kyc.pointInTime ?? 0;
    const postAlert = kyc.postAlert ?? 0;
    const undated = kyc.undated ?? 0;
    if (supplied > 0 && pointInTime === supplied) return 'dated before their alerts';

    const parts: string[] = [];
    if (pointInTime) parts.push(`${count(pointInTime)} dated before their alerts`);
    if (postAlert) parts.push(`${count(postAlert)} dated after`);
    if (undated) parts.push(`${count(undated)} undated`);
    return parts.length ? parts.join(', ') : null;
}

/** "kyc.csv, 16 of 16 customers, dated before their alerts", or null with no KYC. */
export function kycSummary(kyc: KycSummary | null | undefined): string | null {
    if (!kyc) return null;
    const parts: string[] = [];
    if (kyc.sourceName) parts.push(sampleName(kyc.sourceName));
    const matched = kyc.suppliedCustomers ?? kyc.matched;
    if (matched !== null && matched !== undefined) {
        const total = matched + (kyc.customerMasterCustomers ?? 0);
        parts.push(`${count(matched)} of ${count(total)} customers`);
    }
    const dating = kycDating(kyc);
    if (dating) parts.push(dating);
    return parts.join(', ');
}

/** How the loader identified a supplied column, for someone checking the mapping. */
export function columnMethodLabel(method: ColumnMethod | null | undefined): string {
    switch (method) {
        case 'exact': return 'Exact name';
        case 'alias': return 'Known alias';
        case 'human': return 'Confirmed by a person';
        case 'llm': return 'Identified by model';
        default: return method ? String(method) : 'Unknown';
    }
}

/** Comparison modes, as a reader would say them. */
export function comparisonModeLabel(mode: string | null | undefined): string {
    switch (mode) {
        case 'alert_only': return 'Alert only';
        case 'kyc_enriched': return 'With KYC';
        case 'context_enriched': return 'With context';
        case 'full_features': return 'Full features';
        default: return mode ? mode.replace(/_/g, ' ') : '—';
    }
}
